import {
  BUILD_COMMANDS,
  keywordEngine,
  localEngine,
  SUGGESTED_QUESTIONS,
  type Answer,
  type Attachment,
  type AskContext,
  type BuildStore,
  type Turn,
} from '@manudev/assistant';
import type { HeroData } from '@/hero/data';
import { localGenerator, useLocalModel } from '@/llm/model';
import { useSelectionStore } from '@/state/selection';
import { hero } from '@/state/tour';
import { saveBytes } from '@/store/download';
import { useHeroStore } from '@/store/store';

export type { Answer };
export const SUGGESTED = SUGGESTED_QUESTIONS.filter((q) => !BUILD_COMMANDS.includes(q));
export const COMMANDS = BUILD_COMMANDS;

// questions: browser model once loaded, keyword rules otherwise; changes: always the rules
export async function ask(
  question: string,
  data: HeroData | null,
  history: Turn[] = [],
): Promise<Answer> {
  const ctx = { ...contextOf(data), history };
  const answer = await answered(question, ctx);
  saveExports(answer);
  return answer;
}

async function answered(question: string, ctx: AskContext): Promise<Answer> {
  if (useLocalModel.getState().status === 'ready') {
    try {
      return await localEngine({ generator: localGenerator }).answer(question, ctx);
    } catch (err) {
      const message = (err as Error)?.message ?? String(err);
      console.warn('[assistant] browser model failed, falling back to rules', err);
      const answer = await keywordEngine.answer(question, ctx);
      return { ...answer, note: `browser model failed: ${message}` };
    }
  }
  return keywordEngine.answer(question, ctx);
}

export function contextOf(data: HeroData | null): AskContext {
  const store = useHeroStore.getState();
  return {
    heroName: hero.name,
    elements: data ? [...data.byId.values()] : [],
    bounds: useSelectionStore.getState().bounds ?? undefined,
    ...(store.status === 'ready' ? { store: buildStore() } : {}),
  };
}

// The worker proxy answers every op with a promise where the Node store answers straight away,
// which is the whole reason BuildStore exists. The rest of the shape already lines up.
function buildStore(): BuildStore {
  const state = () => useHeroStore.getState();
  return {
    get schema() {
      return state().schema ?? 'IFC2X3';
    },
    get site() {
      return state().site;
    },
    storeys: () => state().storeys,
    elements: () => state().elements,
    newIds: () => state().newIds,
    anchor: (storeyId) => state().anchor(storeyId),
    wallSegments: (storeyId) => state().wallSegments(storeyId),
    spaceFootprints: (storeyId) => state().spaceFootprints(storeyId),
    addWall: (storeyId, params) => state().addWall(storeyId, params),
    addSlab: (storeyId, params) => state().addSlab(storeyId, params),
    addRoof: (storeyId, params) => state().addRoof(storeyId, params),
    addDoor: (storeyId, params) => state().addDoor(storeyId, params),
    addWindow: (storeyId, params) => state().addWindow(storeyId, params),
    addSpace: (storeyId, params) => state().addSpace(storeyId, params),
    addStorey: (name, elevation) => state().addStorey(name, elevation),
    addRaw: (entityType, attributes, opts) => state().addRaw(entityType, attributes, opts),
    materialId: (name) => state().materialId(name),
    guid: () => state().guid(),
    remove: (id) => state().remove(id),
    beginBatch: () => state().beginBatch(),
    endBatch: () => state().endBatch(),
    undo: () => state().undo(),
    redo: () => state().redo(),
    exportStep: () => state().exportStep(),
    mesh: (ids) => state().mesh(ids),
    plan: (opts) => state().plan(opts),
    section: (opts) => state().section(opts),
    checkIds: (idsXml) => state().checkIds(idsXml),
    diff: () => state().diff(),
  };
}

function saveExports(answer: Answer) {
  for (const call of answer.calls) {
    const out = call.output as { bytes?: Uint8Array; attachment?: Attachment } | null;
    if (call.tool === 'build' && out?.bytes) saveBytes(out.bytes, `${hero.propsLayer}-edited.ifc`);
    if (out?.attachment) saveAttachment(out.attachment);
  }
}

export function saveAttachment(file: Attachment) {
  const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0));
  saveBytes(bytes, `${hero.propsLayer}-${file.name}`, file.mime);
}
